import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import styles from "../css/css_pages/newIssue.module.css";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import config from "../../config";
import PopupMessage from "../layouts/PopupMessage";

const TicketProduct = {
  AI_BANKER: "ai-banker",
  COOP_MIS: "coop-mis",
  POS: "pos"
};

const TicketType = {
  NOT_WORKING: "not-working",
  DATA_CORRECTION: "data-correction"
};

const TicketPriority = {
  NORMAL: "normal",
  MEDIUM: "medium",
  HIGH: "high"
};

const NewIssueForm = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [description, setDescription] = useState("");
  const [issuePhone, setIssuePhone] = useState("");
  const [date, setDate] = useState("");
  const [tso, setTSO] = useState("");
  const [issueType, setIssueType] = useState("");
  const [priority, setPriority] = useState("");
  const [totalDueAmount, setTotalDueAmount] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [pastDueDate, setPastDueDate] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [product, setProduct] = useState("");
  const [fullName, setFullName] = useState("");
  const [managerPhone, setManagerPhone] = useState("");
  const [useSavedPhone, setUseSavedPhone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "35px",
      borderColor: "#e2e8f0",
      fontSize: "12px"
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "12px",
      backgroundColor: state.isSelected ? "#e0e0e0" : "white",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  const fetchClients = async () => {
    try {
      setIsLoading(true);
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found.");

      const response = await fetch(`${config.API_URL}/tso-online/get-all-clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setClients(
          data.map((client) => ({
            value: client.id,
            label: client.customer_name,
            phone: client.phone,
            mobile: client.client_mobile,
            address: client.address,
            tso_name: client.tso_name,
            tso_id: client.tso_id,
            fullName: client.customer_name,
            managerPhone: client.manager,
          }))
        );
      } else {
        const errorData = await response.json();
        showPopupMessage(errorData.message || "Failed to fetch clients.");
      }
    } catch (error) {
      showPopupMessage("Error loading client data.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFinanceDetails = async (client_id) => {
    try {
      setIsLoading(true);
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found.");

      const response = await fetch(
        `${config.API_URL}/tso-online/get-finance-details?client_id=${client_id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDueDate(data.due_date_min || "none");
        setPastDueDate(data.past_due_date || "none");
        setTotalDueAmount(data.total_due || "0");
      }
    } catch (error) {
      console.error("Error fetching finance data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const currentDate = new Date().toISOString().slice(0, 10);
    setDate(currentDate);
    fetchClients();
  }, []);

  const formatPhoneNumber = (phone) => {
    return phone.replace(/\D/g, '');
  };

  const handleClientChange = (selectedOption) => {
    setSelectedClient(selectedOption);
    if (selectedOption) {
      fetchFinanceDetails(selectedOption.value);
      setPhone(selectedOption.phone);
      setAddress(selectedOption.address);
      setTSO(`${selectedOption.tso_name} | emp no: ${selectedOption.tso_id}`);
      setFullName(selectedOption.fullName);
      setManagerPhone(selectedOption.managerPhone);
      setUseSavedPhone(false);
      setIssuePhone(selectedOption.mobile);
    } else {
      setPhone("");
      setAddress("");
      setTSO("");
      setDueDate("");
      setPastDueDate("");
      setTotalDueAmount("");
      setIssuePhone("");
      setFullName("");
      setManagerPhone("");
      setUseSavedPhone(false);
    }
  };

  const handleUseSavedPhoneChange = (e) => {
    const isChecked = e.target.checked;
    setUseSavedPhone(isChecked);
    if (isChecked && selectedClient) {
      setIssuePhone(selectedClient.mobile);
    } else {
      setIssuePhone("");
    }
  };

  const handleIssuePhoneChange = (e) => {
    if (useSavedPhone) return;
    const formattedValue = e.target.value.replace(/\D/g, '').slice(0, 10);
    setIssuePhone(formattedValue);
  };

  const handleFileChange = (e) => {
    try {
      const files = Array.from(e.target.files);
      const validFiles = files.filter(file => file.size <= 5 * 1024 * 1024);
      setUploadedFiles((prevFiles) => [...prevFiles, ...validFiles]);
    } catch (error) {
      showPopupMessage("Error processing files.");
    }
  };

  const handleFileClick = (file) => {
    try {
      setSelectedFile(file);
      setShowModal(true);
    } catch (error) {
      showPopupMessage("Error previewing file.");
    }
  };

  const handleRemoveFile = (fileToRemove) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedFile(null);
  };

  const showPopupMessage = (message) => {
    setAlertMessage(message);
  };

  const closePopupMessage = () => {
    setAlertMessage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const validationErrors = [];
    if (!selectedClient) validationErrors.push("Please select a client");
    if (!description.trim()) validationErrors.push("Please provide a description");
    if (!issueType) validationErrors.push("Please select an issue type");
    if (!priority) validationErrors.push("Please select a priority level");
    if (!product) validationErrors.push("Please select a product");

    if (validationErrors.length > 0) {
      showPopupMessage(validationErrors.join("\n"));
      return;
    }

    setIsSubmitting(true);

    try {
      const formattedPhone = issuePhone ? formatPhoneNumber(issuePhone) : "";
      const token = Cookies.get("authToken");
      if (!token) {
        showPopupMessage("Authentication token not found.");
        navigate("/login");
        return;
      }

      const formData = new FormData();
      formData.append('issue', description.trim());
      formData.append('customer_id', selectedClient.value);
      formData.append('ticket_type', issueType);
      formData.append('ticket_priority', priority);
      formData.append('product', product);
      if (formattedPhone) formData.append('contact_person_phone_number', formattedPhone);
      formData.append('ticket_status', 'pending');

      uploadedFiles.forEach(file => formData.append('files', file));

      const response = await fetch(`${config.API_URL}/tso-online/create-ticket`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create ticket");
      }

      showPopupMessage(`Ticket created successfully for ${selectedClient.label}`);

      setTimeout(() => {
        navigate("/dashboard");
        setSelectedClient(null);
        setDescription("");
        setIssuePhone("");
        setIssueType("");
        setPriority("");
        setProduct("");
        setUploadedFiles([]);
        setUseSavedPhone(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setIsSubmitting(false);
      }, 2000);

    } catch (error) {
      showPopupMessage(error.message || "Error creating ticket");
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      {alertMessage && <PopupMessage message={alertMessage} onClose={closePopupMessage} />}
      <h1 className={styles.formTitle}>New Issue Form</h1>
      
      <div className={styles.formWrapper}>
        <div className={styles.leftSection}>
          <div className={styles.card}>
            <h3>Client</h3>
            <div className={styles.formGroup}>
              <label>Client <span className={styles.required}>*</span></label>
              <Select
                className={styles.customSelect}
                value={selectedClient}
                onChange={handleClientChange}
                options={clients}
                isClearable
                placeholder={isLoading ? "Loading..." : "Select client"}
                required
                styles={customSelectStyles}
                isLoading={isLoading}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Product <span className={styles.required}>*</span></label>
              <div className={styles.productRadioGrid}>
                {Object.values(TicketProduct).map((prod) => (
                  <label key={prod} className={styles.productRadioOption}>
                    <input
                      type="radio"
                      name="product"
                      value={prod}
                      checked={product === prod}
                      onChange={() => setProduct(prod)}
                    />
                    <span className={styles.radioLabel}>{prod}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Issue Type <span className={styles.required}>*</span></label>
              <select
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
                required
                className={styles.formGroupSelect}
              >
                <option value="">Select issue type</option>
                {Object.values(TicketType).map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Priority <span className={styles.required}>*</span></label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                required
                className={styles.formGroupSelect}
              >
                <option value="">Select priority</option>
                {Object.values(TicketPriority).map((priorityLevel) => (
                  <option key={priorityLevel} value={priorityLevel}>{priorityLevel}</option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Date <span className={styles.required}>*</span></label>
              <input
                type="date"
                value={date}
                disabled
                required
                className={styles.formGroupInput}
              />
            </div>
          </div>

          <div className={styles.card}>
            <h3>Description</h3>
            <div className={styles.formGroup}>
              <label>Description <span className={styles.required}>*</span></label>
              <textarea
                placeholder="Describe the issue"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                className={styles.formGroupTextarea}
              />
            </div>

            <div className={styles.checkboxContainer}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={useSavedPhone}
                  onChange={handleUseSavedPhoneChange}
                  disabled={!selectedClient}
                />
                <span>Use client's saved phone number</span>
              </label>
            </div>

            <div className={styles.formGroup}>
              <label>Contact Phone</label>
              <input
                type="tel"
                value={issuePhone}
                onChange={handleIssuePhoneChange}
                maxLength="10"
                disabled={useSavedPhone}
                placeholder="10-digit number"
                className={styles.formGroupInput}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Attachments</label>
              <input
                type="file"
                onChange={handleFileChange}
                multiple
                accept=".jpg,.jpeg,.png,.pdf,.zip,.txt"
                ref={fileInputRef}
                className={styles.fileInput}
              />
            </div>

            <ul className={styles.uploadedFilesList}>
              {uploadedFiles.map((file, index) => (
                <li key={index}>
                  <span onClick={() => handleFileClick(file)}>
                    {file.name} ({(file.size / 1024).toFixed(2)} KB)
                  </span>
                  <span className={styles.removeFileBtn} onClick={() => handleRemoveFile(file)}>
                    &#10005;
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className={styles.rightSection}>
          <div className={styles.card}>
            <h3>Payments</h3>
            <div className={styles.formGroup}>
              <label>Total Due:</label>
              <span>{totalDueAmount}</span>
            </div>
            <div className={styles.formGroup}>
              <label>Due Date:</label>
              <span>{dueDate || "none"}</span>
            </div>
            <div className={styles.formGroup}>
              <label>Past Due:</label>
              <span>{pastDueDate || "none"}</span>
            </div>
          </div>

          <div className={styles.card}>
            <h3>Information</h3>
            <div className={styles.formGroup}>
              <label>Phone:</label>
              <span>{phone || "-"}</span>
            </div>
            <div className={styles.formGroup}>
              <label>Address:</label>
              <span>{address || "-"}</span>
            </div>
            <div className={styles.formGroup}>
              <label>TSO:</label>
              <span>{tso || "-"}</span>
            </div>
            <div className={styles.formGroup}>
              <label>Full Name:</label>
              <span>{fullName || "-"}</span>
            </div>
            <div className={styles.formGroup}>
              <label>Manager Phone:</label>
              <span>{managerPhone || "-"}</span>
            </div>
            
            <button
              className={styles.submitButton}
              onClick={handleSubmit}
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting ? "Submitting..." : "Submit Ticket"}
            </button>
          </div>
        </div>
      </div>

      {showModal && selectedFile && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <span className={styles.close} onClick={handleModalClose}>&times;</span>
            <h3>File Preview</h3>
            <p>Name: {selectedFile.name}</p>
            <p>Size: {(selectedFile.size / 1024).toFixed(2)} KB</p>

            {selectedFile.type.startsWith("image/") ? (
              <img
                src={URL.createObjectURL(selectedFile)}
                alt={selectedFile.name}
                className={styles.modalImage}
              />
            ) : (
              <div className={styles.filePreviewPlaceholder}>
                <p>No preview available</p>
                <a
                  href={URL.createObjectURL(selectedFile)}
                  download={selectedFile.name}
                >
                  Download File
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewIssueForm;